import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { Option } from "components/ui/combobox/types";
import UIcon from "lib/icons/untitled_ui/UIcon";

type Props = {
  options: Option[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  /** Names the control for assistive technology; there is no visible label beside it. */
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  /** Names the control that unsets the value. Without one, the value can only be replaced. */
  clearLabel?: string;
  disabled?: boolean;
};

/**
 * The one control the mapping form picks anything with.
 *
 * A search box rather than a plain dropdown because an object routinely has a hundred or more
 * fields, and the CRM's own label is not always guessable from the column — an opportunity's
 * `name` is labelled "Topic". The object picker is short enough not to need the search, but shares
 * the control so the two rows of a mapping read as one.
 */
export default function MappingCombobox({
  options,
  value,
  onChange,
  label,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  clearLabel,
  disabled,
}: Props): JSX.Element {
  const selected = options.find((option) => option.value === value);

  return (
    <Combobox
      items={options}
      value={value ? [value] : []}
      onChange={(values) => onChange(values.length > 0 ? String(values[0]) : undefined)}
      multiple={false}
    >
      {(combobox) => (
        <ComboboxContent
          open={combobox.open}
          setOpen={combobox.setOpen}
          trigger={
            // The clear control is a sibling of the trigger rather than inside it: a button cannot
            // nest in a button, and as a span it was reachable only with a pointer.
            <TriggerRow>
              <Trigger
                type="button"
                disabled={disabled}
                aria-label={label}
                onClick={() => {
                  if (!combobox.open) combobox.setFilterValue("");
                  combobox.setOpen(!combobox.open);
                }}
              >
                <TriggerText isPlaceholder={!selected}>
                  {selected?.label ?? placeholder}
                </TriggerText>
                <UIcon icon="chevronDown" size={16} color="sysTextSecondary" />
              </Trigger>
              {clearLabel && value && !disabled && (
                <ClearButton
                  type="button"
                  aria-label={clearLabel}
                  onClick={() => onChange(undefined)}
                >
                  <UIcon icon="xClose" size={14} color="sysTextSecondary" />
                </ClearButton>
              )}
            </TriggerRow>
          }
        >
          <ComboboxInput
            placeholder={searchPlaceholder}
            filterValue={combobox.filterValue}
            setFilterValue={combobox.setFilterValue}
            open={combobox.open}
            setOpen={combobox.setOpen}
            value={[]}
            items={combobox.items}
            multiple={combobox.multiple}
            inputRef={combobox.inputRef}
            inputElementRef={combobox.inputElementRef}
          />
          <ComboboxEmpty
            filteredItems={combobox.filteredItems}
            filterValue={combobox.filterValue}
            disableAddingOptions
          >
            {emptyMessage}
          </ComboboxEmpty>
          <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
            <ComboboxCollection options={combobox.filteredItems as Option[]}>
              {(option: Option) => (
                <ComboboxItem
                  key={String(option.value)}
                  option={option}
                  value={combobox.value}
                  selectItem={combobox.selectItem}
                  multiple={combobox.multiple}
                  highlightedIndex={combobox.highlightedIndex}
                  filteredItems={combobox.filteredItems}
                >
                  <Text>{option.label}</Text>
                </ComboboxItem>
              )}
            </ComboboxCollection>
          </ComboboxList>
        </ComboboxContent>
      )}
    </Combobox>
  );
}

const TriggerRow = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundDefault,
  border: `1px solid ${theme.colors.sysBorderPrimary}`,
}));

const Trigger = styled.button({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  flex: 1,
  minWidth: 0,
  padding: "8px 12px",
  border: "none",
  background: "none",
  cursor: "pointer",
  "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
});

// Not named `placeholder`: that is a real DOM attribute, so Emotion forwards it to the element.
const TriggerText = styled(Text)<{ isPlaceholder: boolean }>(({ theme, isPlaceholder }) => ({
  color: isPlaceholder ? theme.colors.sysTextPlaceholder : theme.colors.sysTextDefault,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ClearButton = styled.button({
  display: "flex",
  alignItems: "center",
  padding: "8px 12px 8px 0",
  border: "none",
  background: "none",
  cursor: "pointer",
});
